import React, { useState } from 'react';
import { View, Dimensions, ScrollView, StyleSheet, Text } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { VictoryChart, VictoryPolarAxis, VictoryArea, VictoryGroup, VictoryTheme } from 'victory-native';
import { Picker } from '@react-native-picker/picker';
import { baseColors } from "@Constants/baseStyles";

const { width, height } = Dimensions.get("window");

// Nouvelle liste de labels
const AXES_CONFIG = [
  { label: ['Équilibre', 'alimentaire'], key: 'diet', groupName: 'Equilibre alimentaire' },
  { label: ['Gras,', 'salé et', 'sucré'], key: 'fatsugar', groupName: 'Gras, salé et sucré' },
  { label: ['Sédentarité'], key: 'sedentary', groupName: 'Sédentarité' },
  { label: ['Activité', 'physique'], key: 'activity', groupName: 'Activité physique' },
  { label: ['Composante', 'physique'], key: 'physical', groupName: 'Composante physique' },
  { label: ['Composante', 'psy'], key: 'psy', groupName: 'Composante psy' },
  { label: ['Composante', 'sociale'], key: 'social', groupName: 'Composante sociale' },
  { label: ['Composante', 'environnement'], key: 'environment', groupName: 'Composante environnement' },
];

// Fonction pour traiter les données de l'API
const processData = (apiResponse) => {
  if (!apiResponse) return [];

  const processedData = [];
  const programs = Object.keys(apiResponse);

  programs.forEach((programName) => {
    const programData = apiResponse[programName];
    const dates = Object.keys(programData);
    const datasets = [];

    dates.forEach((date) => {
      const data = AXES_CONFIG.map((axis) => {
        const item = programData[date].find(i => i.groupName === axis.groupName);
        return item ? (item.percentScore || 0) : 0;
      });

      datasets.push({
        name: date.replace(/-\d+$/, ""),
        data: data,
        color: dates.indexOf(date) % 2 === 0 ?
          `${baseColors.green.primary}80` :
          `${baseColors.orange.primary}80`,
        date: date,
      });
    });

    processedData.push({
      programName: programName,
      datasets: datasets,
      color: programName.includes("5.0") ?
        baseColors.green.primary :
        baseColors.orange.primary,
    });
  });

  return processedData;
};

// Composant pour afficher le tableau des données d'un programme
const DataTable = ({ program }) => {
  const [selectedDatasetIndex, setSelectedDatasetIndex] = useState(0);
  const selectedDataset = program?.datasets[selectedDatasetIndex] || {};

  return (
    <View style={styles.tableContainer}>
      {/* Sélecteur de dataset */}
      <View style={styles.pickerContainer}>
        <Picker
          selectedValue={selectedDatasetIndex}
          style={styles.picker}
          onValueChange={(itemValue) => setSelectedDatasetIndex(itemValue)}
        >
          {program.datasets.map((dataset, index) => (
            <Picker.Item
              key={index}
              label={dataset.name}
              value={index}
            />
          ))}
        </Picker>
      </View>

      {/* En-têtes du tableau */}
      <View style={styles.tableHeader}>
        <Text style={[styles.tableCell, styles.headerText, { flex: 2 }]}>Catégorie</Text>
        <Text style={[styles.tableCell, styles.headerText, { flex: 1 }]}>Score (%)</Text>
      </View>

      {/* Lignes du tableau */}
      {AXES_CONFIG.map((axis, index) => (
        <View key={index} style={styles.tableRow}>
          <Text style={[styles.tableCell, { flex: 2 }]}>{axis.groupName}</Text>
          <Text style={[styles.tableCell, { flex: 1 }]}>
            {selectedDataset.data ? selectedDataset.data[index] || 'N/A' : 'N/A'}
          </Text>
        </View>
      ))}
    </View>
  );
};

// Composant pour afficher le radar chart
const RadarChartComponent = ({ programData }) => {
  return (
    <>
      {programData.map((program, index) => (
        <View
          key={index}
          style={[
            styles.chartContainer,
            index === 0 && { marginTop: 30 }
          ]}
        >
          <Text style={styles.programTitle}>{program.programName}</Text>

          <VictoryChart
            polar
            width={width - 30}
            height={height * 0.5}
            theme={VictoryTheme.material}
            domain={{ y: [0, 100] }}
            padding={{ top: 50, bottom: 50, left: 70, right: 70 }}
          >
            <VictoryGroup
              style={{
                data: {
                  fillOpacity: 0.4,
                  strokeWidth: 2,
                },
              }}
            >
              {program.datasets.map((dataset, datasetIndex) => (
                <VictoryArea
                  key={datasetIndex}
                  data={dataset.data.map((value, i) => ({ x: i + 1, y: value }))}
                  style={{
                    data: {
                      fill: dataset.color,
                      stroke: dataset.color.replace('80', 'FF'),
                    },
                  }}
                />
              ))}
            </VictoryGroup>

            {/* Cercles concentriques */}
            <VictoryPolarAxis
              dependentAxis
              style={{
                axis: { stroke: "none" },
                grid: {
                  stroke: "#999",
                  strokeWidth: 1,
                  opacity: 0.8,
                },
                tickLabels: {
                  fontSize: 10,
                  fill: "#333",
                }
              }}
              tickValues={[20, 40, 60, 80, 100]}
              tickFormat={(t) => t}
            />

            {/* Lignes radiales */}
            <VictoryPolarAxis
              tickValues={AXES_CONFIG.map((_, i) => i + 1)}
              tickFormat={() => ""}
              style={{
                axis: { stroke: "none" },
                grid: {
                  stroke: "#999",
                  strokeWidth: 1,
                  opacity: 0.8,
                },
              }}
            />

            {/* Labels des axes */}
            <VictoryPolarAxis
              tickValues={AXES_CONFIG.map((_, i) => i + 1)}
              tickFormat={AXES_CONFIG.map(axis => axis.label.join('\n'))}
              style={{
                tickLabels: {
                  fontSize: 10,
                  padding: 20,
                  fill: "#333"
                },
                axis: { stroke: "none" },
                grid: { stroke: "none" },
              }}
            />
          </VictoryChart>

          {/* Légende des couleurs */}
          <View style={styles.legendContainer}>
            {program.datasets.map((dataset, index) => (
              <View key={index} style={styles.legendItem}>
                <View style={[styles.legendColor, { backgroundColor: dataset.color.replace('80', 'FF') }]} />
                <Text style={styles.legendText}>{dataset.name}</Text>
              </View>
            ))}
          </View>

          {/* Tableau des données */}
          <DataTable program={program} />
        </View>
      ))}
    </>
  );
};

// Styles
const styles = StyleSheet.create({
  chartContainer: {
    marginBottom: 20,
    alignItems: 'center',
    width: '100%',
  },
  programTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
    width: '100%',
    textAlign: 'center',
  },
  legendContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 10,
    flexWrap: 'wrap',
    width: '100%',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 10,
    marginBottom: 10,
  },
  legendColor: {
    width: 12,
    height: 12,
    marginRight: 5,
    borderRadius: 2,
  },
  legendText: {
    fontSize: 12,
  },
  tableContainer: {
    width: '90%',
    marginTop: 20,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 5,
    overflow: 'hidden',
    padding: 10,
  },
  pickerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  picker: {
    flex: 1,
    height: 40,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  tableCell: {
    paddingHorizontal: 8,
    fontSize: 12,
  },
  headerText: {
    fontWeight: 'bold',
  },
});

// Composant principal
export const ScoreDECard = ({ data: apiResponse }) => {
  const processedData = processData(apiResponse);

  if (processedData.length === 0) {
    return (
      <SafeAreaView style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <Text>Aucune donnée à afficher</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, width: '100%' }}>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 5,
          alignItems: 'center',
          width: '100%',
        }}
        showsHorizontalScrollIndicator={false}
      >
        <RadarChartComponent programData={processedData} />
      </ScrollView>
    </SafeAreaView>
  );
};
